<template>
    <header class="ei-master-header">
        <span class="ei-page-title">원단위 관리</span>
        <div class="ei-topbar-filter">
            <span class="ei-topbar-filter-label">조회</span>
            <div class="ei-period-seg" role="group" aria-label="연간 또는 월간 조회">
                <button
                    type="button"
                    class="ei-period-seg-btn"
                    :class="{ 'ei-period-seg-btn--active': dateType === ENINT_DATE_TYPE_YEAR }"
                    @click="$emit('set-date-type', ENINT_DATE_TYPE_YEAR)"
                >
                    연간
                </button>
                <button
                    type="button"
                    class="ei-period-seg-btn"
                    :class="{ 'ei-period-seg-btn--active': dateType === ENINT_DATE_TYPE_MONTH }"
                    @click="$emit('set-date-type', ENINT_DATE_TYPE_MONTH)"
                >
                    월간
                </button>
            </div>
            <div v-if="dateType === ENINT_DATE_TYPE_YEAR" class="ei-topbar-dp-with-quick">
                <button
                    type="button"
                    class="ei-topbar-step-btn"
                    :disabled="yearQuickPrevDisabled"
                    title="이전 연도"
                    aria-label="이전 연도"
                    @click="$emit('quick-shift', -1)"
                >
                    <i class="fas fa-chevron-left" aria-hidden="true"></i>
                </button>
                <DatePicker
                    :value="yearPickerModel"
                    type="year"
                    format="YYYY"
                    value-type="format"
                    :clearable="false"
                    class="ei-topbar-year-dp"
                    @input="$emit('update:yearPickerModel', $event)"
                    @change="$emit('year-change')"
                />
                <button
                    type="button"
                    class="ei-topbar-step-btn"
                    :disabled="yearQuickNextDisabled"
                    title="다음 연도"
                    aria-label="다음 연도"
                    @click="$emit('quick-shift', 1)"
                >
                    <i class="fas fa-chevron-right" aria-hidden="true"></i>
                </button>
            </div>
            <div v-else class="ei-topbar-dp-with-quick">
                <button
                    type="button"
                    class="ei-topbar-step-btn"
                    :disabled="monthQuickPrevDisabled"
                    title="이전 달"
                    aria-label="이전 달"
                    @click="$emit('quick-shift', -1)"
                >
                    <i class="fas fa-chevron-left" aria-hidden="true"></i>
                </button>
                <DatePicker
                    :value="monthPickerModel"
                    type="month"
                    format="YYYY-MM"
                    value-type="format"
                    :clearable="false"
                    class="ei-topbar-period-dp"
                    @input="$emit('update:monthPickerModel', $event)"
                    @change="$emit('month-change')"
                />
                <button
                    type="button"
                    class="ei-topbar-step-btn"
                    :disabled="monthQuickNextDisabled"
                    title="다음 달"
                    aria-label="다음 달"
                    @click="$emit('quick-shift', 1)"
                >
                    <i class="fas fa-chevron-right" aria-hidden="true"></i>
                </button>
            </div>
        </div>
    </header>
</template>

<script>
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import { currentYm, ENINT_DATE_TYPE_YEAR, ENINT_DATE_TYPE_MONTH, ymAddMonths } from "../utils/enIntUi.js";

export default {
    name: "EnIntMasterHeader",
    components: { DatePicker },
    props: {
        dateType: { type: String, required: true },
        dateValue: { type: Number, required: true },
        yearPickerModel: { type: String, required: true },
        monthPickerModel: { type: String, required: true },
    },
    data() {
        return {
            ENINT_DATE_TYPE_YEAR,
            ENINT_DATE_TYPE_MONTH,
        };
    },
    computed: {
        yearBounds() {
            const dv = Number(this.dateValue);
            let y = dv;
            if (dv > 200000) y = Math.floor(dv / 100);
            if (!Number.isFinite(y) || y < 2000) y = new Date().getFullYear();
            if (y > 2100) y = 2100;
            return y;
        },
        yearQuickPrevDisabled() {
            return this.yearBounds <= 2000;
        },
        yearQuickNextDisabled() {
            return this.yearBounds >= 2100;
        },
        monthBoundsYm() {
            const ym = Number(this.dateValue);
            if (Number.isFinite(ym) && ym > 200000) return ym;
            return currentYm();
        },
        monthQuickPrevDisabled() {
            return ymAddMonths(this.monthBoundsYm, -1) < 200001;
        },
        monthQuickNextDisabled() {
            return ymAddMonths(this.monthBoundsYm, 1) > 210012;
        },
    },
};
</script>

<style scoped>
.ei-master-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 11px 18px;
    background: linear-gradient(135deg, var(--ei-navy, #0a3d5c) 0%, #0c5271 100%);
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    flex-shrink: 0;
}

.ei-page-title {
    font-size: 17px;
    font-weight: 700;
    color: #f0f6ff;
    letter-spacing: -0.02em;
}

.ei-topbar-filter {
    display: flex;
    align-items: center;
    gap: 10px;
}

.ei-topbar-filter-label {
    font-size: 12px;
    font-weight: 700;
    color: rgba(224, 234, 255, 0.9);
    white-space: nowrap;
}

.ei-period-seg {
    display: inline-flex;
    align-items: stretch;
    gap: 2px;
    padding: 3px;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.12);
}

.ei-period-seg-btn {
    margin: 0;
    padding: 6px 14px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: -0.01em;
    line-height: 1.2;
    color: rgba(240, 246, 255, 0.78);
    background: transparent;
    border: 0;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: max-content;
    cursor: pointer;
    transition:
        background 0.14s ease,
        color 0.14s ease,
        box-shadow 0.14s ease;
}

.ei-period-seg-btn:hover:not(.ei-period-seg-btn--active) {
    background: rgba(255, 255, 255, 0.1);
    color: rgba(252, 252, 255, 0.95);
}

.ei-period-seg-btn--active {
    background: rgba(255, 255, 255, 0.94);
    color: var(--ei-navy, #0a3d5c);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.14);
}

.ei-period-seg-btn:focus {
    outline: none;
}

.ei-period-seg-btn:focus-visible {
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.45);
}

.ei-period-seg-btn--active:focus-visible {
    box-shadow:
        0 1px 2px rgba(0, 0, 0, 0.14),
        0 0 0 2px rgba(255, 255, 255, 0.45);
}

.ei-topbar-dp-with-quick {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.ei-topbar-step-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    padding: 0;
    border-radius: 6px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background: rgba(255, 255, 255, 0.08);
    color: rgba(240, 246, 255, 0.92);
    font-size: 11px;
    cursor: pointer;
    transition:
        background 0.14s ease,
        border-color 0.14s ease,
        color 0.14s ease;
}

.ei-topbar-step-btn:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.16);
    border-color: rgba(255, 255, 255, 0.32);
}

.ei-topbar-step-btn:focus {
    outline: none;
}

.ei-topbar-step-btn:focus-visible {
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.4);
}

.ei-topbar-step-btn:disabled {
    opacity: 0.35;
    cursor: not-allowed;
}

.ei-topbar-year-dp :deep(.mx-input),
.ei-topbar-period-dp :deep(.mx-input) {
    border-radius: 7px;
    border: 1px solid rgba(255, 255, 255, 0.22);
    background: rgba(255, 255, 255, 0.12);
    color: #f0f6ff;
    font-size: 12px;
    font-weight: 600;
    min-width: 118px;
}

.ei-topbar-year-dp :deep(.mx-input) {
    min-width: 88px;
}

.ei-topbar-year-dp :deep(.mx-input::placeholder),
.ei-topbar-period-dp :deep(.mx-input::placeholder) {
    color: rgba(240, 246, 255, 0.7);
}

.ei-topbar-year-dp :deep(.mx-input:hover),
.ei-topbar-year-dp :deep(.mx-input:focus),
.ei-topbar-period-dp :deep(.mx-input:hover),
.ei-topbar-period-dp :deep(.mx-input:focus) {
    border-color: rgba(255, 255, 255, 0.34);
    background: rgba(255, 255, 255, 0.18);
}
</style>
